import React, { memo, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Close as CloseIcon } from "@mui/icons-material";

const DESCRIPTION_LIMIT = 160;

const CarouselCard = memo(
  ({
    card,
    headerMediaType,
    buttonTypes,
    numButtons,
    handleRemoveCard,
    handleFileUpload,
    handleCardChange,
    DynamicButtonFields,
  }) => {
    /**
     * 🧠 Inserta variable en descripción cuando se agrega una nueva
     */
    const handleAddVariable = useCallback(() => {
      const nextIndex = card.variables.length + 1;
      const newVar = { name: `{{${nextIndex}}}`, sample: "" };

      const updatedDescription =
        (card.description ? card.description.trim() + " " : "") + newVar.name;

      handleCardChange(card.id, "description", updatedDescription);
      handleCardChange(card.id, "variables", [...card.variables, newVar]);
    }, [card, handleCardChange]);

    /**
     * 🧹 Elimina variable del array y del texto
     */
    const handleRemoveVariable = useCallback(
      (variableName) => {
        const updatedDescription = card.description
          .replaceAll(variableName, "")
          .replace(/\s+/g, " ")
          .trim();

        const updatedVariables = card.variables.filter(
          (v) => v.name !== variableName
        );

        handleCardChange(card.id, "description", updatedDescription);
        handleCardChange(card.id, "variables", updatedVariables);
      },
      [card, handleCardChange]
    );

    /**
     * ✏️ Actualiza el valor de ejemplo de una variable
     */
    const handleSampleChange = useCallback(
      (variableName, newValue) => {
        const updatedVariables = card.variables.map((v) =>
          v.name === variableName ? { ...v, sample: newValue } : v
        );
        handleCardChange(card.id, "variables", updatedVariables);
      },
      [card, handleCardChange]
    );

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 2,
          borderColor: "divider",
          backgroundColor: "rgba(103,80,164,0.04)",
        }}
      >
        <Stack gap={1.5}>
          {/* Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography fontWeight={600}>Card {card.id}</Typography>
            <IconButton onClick={() => handleRemoveCard(card.id)} color="error">
              <CloseIcon />
            </IconButton>
          </Stack>

          {/* File upload */}
          <Button variant="outlined" component="label" sx={{ alignSelf: "flex-start" }}>
            Choose File
            <input
              hidden
              type="file"
              accept={
                headerMediaType === "image"
                  ? "image/png,image/jpeg"
                  : headerMediaType === "video"
                  ? "video/mp4"
                  : "application/pdf"
              }
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(card.id, file);
              }}
            />
          </Button>

          {card.mediaFile && (
            <Typography variant="caption">{card.mediaFile.name}</Typography>
          )}

          {/* Description */}
          <TextField
            multiline
            minRows={3}
            placeholder="Enter Description"
            value={card.description}
            onChange={(e) =>
              handleCardChange(
                card.id,
                "description",
                e.target.value.slice(0, DESCRIPTION_LIMIT)
              )
            }
            helperText={`${card.description.length}/${DESCRIPTION_LIMIT}`}
          />

          {/* Variables */}
          <Button
            startIcon={<Add />}
            variant="outlined"
            size="small"
            onClick={handleAddVariable}
          >
            Add variable
          </Button>

          {card.variables.length > 0 && (
            <Stack direction="column" gap={1}>
              {card.variables.map((v) => (
                <Box
                  key={v.name}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "80px 1fr",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Chip
                    label={v.name}
                    onDelete={() => handleRemoveVariable(v.name)}
                    sx={{
                      borderColor: "primary.main",
                      color: "primary.main",
                    }}
                    variant="outlined"
                  />
                  <TextField
                    placeholder="Define sample variable value for body"
                    size="small"
                    value={v.sample}
                    onChange={(e) =>
                      handleSampleChange(v.name, e.target.value)
                    }
                    fullWidth
                  />
                </Box>
              ))}
            </Stack>
          )}

          {/* Botones dinámicos */}
          {buttonTypes.slice(0, numButtons).map((type, index) => (
            <DynamicButtonFields
              key={index}
              buttonType={type}
              card={card}
              index={index}
              handleCardChange={handleCardChange}
            />
          ))}
        </Stack>
      </Paper>
    );
  }
);

export default CarouselCard;
